import React, { useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PeopleIcon from "@mui/icons-material/People";
import ScienceIcon from "@mui/icons-material/Science";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import PersonIcon from "@mui/icons-material/Person";
import { useNavigate } from "react-router-dom";
import { getUserProfile } from "../../utils/auth";
import CustomIcon from "../../components/CustomIcon";
import RecordProfilePage from "../../components/RecordProfilePage";
import {
  fetchAdminStats,
  fetchAdminProfile,
  fetchUserManagementData,
  fetchPhysicianList,
  fetchUserData,
} from "../../utils/api";
import { ADMIN_ROUTES } from "../../constants/routes";

const maroon = "#500000";

const InfoRow = ({ label, value }) => (
  <Stack direction="row" spacing={1} alignItems="baseline">
    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
      {label}
    </Typography>
    <Typography variant="body2" color="text.secondary">
      {value}
    </Typography>
  </Stack>
);

const ActionCard = ({
  title,
  description,
  buttonLabel,
  icon,
  onClick,
  secondaryButtons,
  onSecondaryClick,
}) => {
  const handleSecondaryButtonClick = async (e, action) => {
    e.stopPropagation();
    console.log(`Secondary button clicked for ${title}: ${action.text}`);

    if (onSecondaryClick) {
      try {
        await onSecondaryClick(action);
      } catch (error) {
        console.error(
          `Error handling secondary button click for ${title}:`,
          error
        );
      }
    }
  };

  return (
    <Card
      elevation={0}
      sx={{
        mb: 2,
        borderRadius: 0,
      }}
    >
      <CardContent>
        <Box
          sx={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          {/* Center divider (md+) */}
          <Divider
            orientation="vertical"
            sx={{
              position: "absolute",
              top: 16,
              bottom: 16,
              left: "50%",
              transform: "translateX(-0.5px)",
              display: { xs: "none", md: "block" },
            }}
          />
          <Box sx={{ flex: "1 1 auto", minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: maroon }}>
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {description}
            </Typography>
          </Box>

          <Box
            sx={{
              flex: { xs: "1 1 100%", md: "0 0 auto" },
              display: "flex",
              justifyContent: "flex-end",
              width: { xs: "100%", md: "auto" },
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            {secondaryButtons &&
              secondaryButtons.map((button, idx) => (
                <Button
                  key={`secondary-${idx}`}
                  size="small"
                  variant="outlined"
                  startIcon={
                    <CustomIcon name={button.icon} viewBox="0 0 18 18" />
                  }
                  onClick={(e) => handleSecondaryButtonClick(e, button)}
                  sx={{
                    borderColor: maroon,
                    color: maroon,
                    textTransform: "none",
                    fontWeight: 700,
                    "&:hover": {
                      backgroundColor: "#f3f3f3",
                      borderColor: maroon,
                    },
                  }}
                >
                  {button.text}
                </Button>
              ))}
            <Button
              size="small"
              variant="contained"
              startIcon={icon}
              onClick={onClick}
              sx={{
                backgroundColor: maroon,
                textTransform: "none",
                fontWeight: 700,
                "&:hover": { backgroundColor: "#4a0000" },
              }}
            >
              {buttonLabel}
            </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

const AdminProfile = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [adminData, setAdminData] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch user profile and admin data in parallel
        const [keycloakProfile, adminProfile, adminStats, userProfileData] =
          await Promise.all([
            getUserProfile(),
            fetchAdminProfile(),
            fetchAdminStats(),
            fetchUserData(),
          ]);

        setProfile(keycloakProfile);
        setAdminData(adminProfile);
        setStats(adminStats);
        setUserProfile(userProfileData);
      } catch (e) {
        console.error("Error loading admin profile data:", e);
        setError(e.message);
        setProfile(null);
        setAdminData(null);
        setStats(null);
        setUserProfile(null);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const displayName =
    userProfile?.firstName && userProfile?.lastName
      ? `${userProfile.firstName} ${userProfile.lastName}`
      : profile?.fullName || profile?.firstName || adminData?.name || "Admin";
  const department =
    userProfile?.departments?.[0] ||
    adminData?.department ||
    profile?.department ||
    "Environmental & Occupational Health";
  const permissions =
    adminData?.permissions || profile?.roles?.join(", ") || "Basic";
  const jobRole =
    userProfile?.jobRoles?.[0] ||
    adminData?.jobRole ||
    profile?.jobRole ||
    "Project manager";
  const purpose =
    userProfile?.purposeForAccess?.[0] ||
    adminData?.purpose ||
    profile?.purpose ||
    "Funded Research";
  const email =
    userProfile?.email ||
    profile?.email ||
    adminData?.email ||
    "joe@clinicalsquared.com";
  const phone =
    userProfile?.workPhoneNumber ||
    userProfile?.mobilePhoneNumber ||
    adminData?.phone ||
    profile?.phone ||
    "202-555-5555";

  // Secondary button click handlers
  const handleSecondaryButtonClick = async (action) => {
    switch (action.text) {
      case "ADD / REMOVE USER":
        try {
          const userData = await fetchUserManagementData();
          console.log("User management data:", userData);
          // You can add navigation or modal display here
        } catch (error) {
          console.error("Error fetching user management data:", error);
        }
        break;
      case "VIEW PHYSICIAN LIST":
        try {
          const physicians = await fetchPhysicianList();
          console.log("Physician list:", physicians);
          // You can add navigation or modal display here
        } catch (error) {
          console.error("Error fetching physician list:", error);
        }
        break;
      default:
        console.log("Unknown secondary button action:", action.text);
    }
  };

  // Loading state
  if (loading) {
    return (
      <RecordProfilePage title="Admin Profile" backTo="/admin" maxWidth={1200}>
        <Box
          sx={{
            p: 0,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "400px",
          }}
        >
          <Box sx={{ textAlign: "center" }}>
            <Typography variant="h6" sx={{ color: maroon, mb: 2 }}>
              Loading admin profile...
            </Typography>
            <Box
              sx={{
                width: 40,
                height: 40,
                border: `4px solid #f3f3f3`,
                borderTop: `4px solid ${maroon}`,
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
                margin: "0 auto",
              }}
            />
          </Box>
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </Box>
      </RecordProfilePage>
    );
  }

  // Error state
  if (error) {
    return (
      <RecordProfilePage title="Admin Profile" backTo="/admin" maxWidth={1200}>
        <Box sx={{ p: 0, textAlign: "center" }}>
          <Typography variant="h6" sx={{ color: "#d32f2f", mb: 2 }}>
            Error loading admin profile
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
            {error}
          </Typography>
          <Button
            variant="contained"
            onClick={() => window.location.reload()}
            sx={{
              backgroundColor: maroon,
              "&:hover": { backgroundColor: "#4a0000" },
            }}
          >
            Retry
          </Button>
        </Box>
      </RecordProfilePage>
    );
  }

  return (
    <RecordProfilePage title="Admin Profile" backTo={ADMIN_ROUTES.DASHBOARD} maxWidth={1200}>
      <Box sx={{ p: 0 }}>
        <Grid container spacing={2}>
          {/* Left: Profile summary */}
          <Grid item xs={12} md={8}>
            <Card
              elevation={1}
              sx={{
                borderRadius: 0,
              }}
            >
              <CardContent>
                <Grid container spacing={2} alignItems="center">
                  <Grid item>
                    <Avatar
                      sx={{
                        width: 72,
                        height: 72,
                        border: `2px solid ${maroon}`,
                      }}
                    >
                      <PersonIcon sx={{ fontSize: 40, color: maroon }} />
                    </Avatar>
                  </Grid>
                  <Grid item xs>
                    <Typography
                      variant="h5"
                      sx={{ fontWeight: 800, color: maroon }}
                    >
                      {displayName}
                    </Typography>
                    <Divider sx={{ my: 1.5 }} />
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <InfoRow label="Department:" value={department} />
                        <InfoRow label="Permissions:" value={permissions} />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <InfoRow label="Job Role:" value={jobRole} />
                        <InfoRow label="Purpose for Access:" value={purpose} />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item>
                    <IconButton size="small">
                      <MoreVertIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Right: Contact card */}
          <Grid item xs={12} md={4}>
            <Card
              elevation={1}
              sx={{
                borderRadius: 0,
              }}
            >
              <CardContent>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 800, color: maroon }}
                  >
                    Contact Information
                  </Typography>
                  <IconButton size="small">
                    <MoreVertIcon />
                  </IconButton>
                </Stack>
                <Divider sx={{ my: 1.5 }} />
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      E-Mail:
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{ mt: 0.5 }}
                    >
                      <EmailIcon fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        {email}
                      </Typography>
                    </Stack>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      Phone Number:
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{ mt: 0.5 }}
                    >
                      <PhoneIcon fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        {phone}
                      </Typography>
                    </Stack>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Account Actions */}
        <Box sx={{ mt: 3, maxWidth: "1000px" }}>
          <Typography variant="h6" sx={{ fontWeight: 800, mb: 2, color: maroon }}>
            Account Actions
          </Typography>

          <ActionCard
            title="Patient Data"
            description="View patient directory and details."
            buttonLabel="VIEW PATIENT DATA"
            icon={<LocalHospitalIcon />}
            onClick={() => navigate(ADMIN_ROUTES.PATIENT_DATA)}
          />

          <ActionCard
            title="Sample Data"
            description="View sample registry and information."
            buttonLabel="VIEW SAMPLE DATA"
            icon={<ScienceIcon />}
            onClick={() => navigate(ADMIN_ROUTES.SAMPLE_DATA)}
          />

          <ActionCard
            title="User Management"
            description="View all users, add/remove users, and update permissions."
            buttonLabel="VIEW ALL USERS"
            icon={<PeopleIcon />}
            onClick={() => navigate(ADMIN_ROUTES.USER_MANAGEMENT)}
            secondaryButtons={[{ text: "ADD / REMOVE USER", icon: "plus-user" }]}
            onSecondaryClick={handleSecondaryButtonClick}
          />

          <ActionCard
            title="Program Directory"
            description="View clinic and physician lists like addresses and contact."
            buttonLabel="OPEN DIRECTORY"
            icon={<MenuBookIcon />}
            onClick={() => navigate(ADMIN_ROUTES.PROGRAMS)}
            secondaryButtons={[
              { text: "VIEW PHYSICIAN LIST", icon: "cross-briefcase" },
            ]}
            onSecondaryClick={handleSecondaryButtonClick}
          />
        </Box>
      </Box>
    </RecordProfilePage>
  );
};

export default AdminProfile;
